/**
 * Planet, cost and price formulas (GDD §3). Pure functions of state.
 *
 * Layering, as in IPM: fleet stats (additive %) × Foundry Rooms × the planet's
 * own Colony and Prospector multipliers. Shipyard hulls multiply alongside Rooms.
 */

import {
  PLANETS, UPGRADE_PRICE_FRACTION, UPGRADE_PRICE_GROWTH, baseCargo, baseMineRate, baseShipSpeed, type PlanetStat,
} from '../content/planets';
import { DISCOUNT_CAP, YIELD_CAP, resource } from '../content/resources';
import { driftDef } from '../content/drifts';
import { marketMult } from './market';
import { planetMults } from './planets';
import { roomCut, roomMult } from './rooms';
import { shipMult } from './ships';
import type { FleetStats } from './stats';
import type { GameState } from './state';

const m = (v: number): number => 1 + v;

function driftPlanetMine(state: GameState, index: number): number {
  const drift = driftDef(state.run.drift);
  let mult = 1;
  for (const band of drift?.planetMine ?? []) if (index >= band.from && index <= band.to) mult *= band.mult;
  return mult;
}

export function mineRate(state: GameState, stats: FleetStats, index: number): number {
  const level = state.run.planets[index]!.levels.mine;
  return baseMineRate(level)
    * m(stats.mine) * m(stats.haste) * driftPlanetMine(state, index)
    * roomMult(state, 'mine') * shipMult(state, 'mine') * planetMults(state, index).mine;
}

export function shipSpeed(state: GameState, stats: FleetStats, index: number): number {
  const level = state.run.planets[index]!.levels.ship;
  return baseShipSpeed(level) * m(stats.ship) * m(stats.haste)
    * roomMult(state, 'ship') * shipMult(state, 'ship') * planetMults(state, index).ship;
}

/** Seconds for one leg of the trip. */
export function legTime(state: GameState, stats: FleetStats, index: number): number {
  return PLANETS[index]!.distance / shipSpeed(state, stats, index);
}

export function cargoCap(state: GameState, stats: FleetStats, index: number): number {
  const level = state.run.planets[index]!.levels.cargo;
  return baseCargo(level) * m(stats.cargo)
    * roomMult(state, 'cargo') * shipMult(state, 'cargo') * planetMults(state, index).cargo;
}

export function haulRate(state: GameState, stats: FleetStats, index: number): number {
  return cargoCap(state, stats, index) / (2 * legTime(state, stats, index));
}

export function throughput(state: GameState, stats: FleetStats, index: number): number {
  return Math.min(mineRate(state, stats, index), haulRate(state, stats, index));
}

export type Bottleneck = 'mine' | 'haul';

export function bottleneck(state: GameState, stats: FleetStats, index: number): Bottleneck {
  return mineRate(state, stats, index) <= haulRate(state, stats, index) ? 'mine' : 'haul';
}

/** Average GC value of one ore unit from this planet, before sell bonuses and market. */
export function planetUnitValue(state: GameState, index: number): number {
  return PLANETS[index]!.ores.reduce((sum, o) => sum + o.share * unitValue(state, o.id), 0);
}

/** GC/s this planet would earn if every ore were sold at today's market. */
export function planetIncome(state: GameState, stats: FleetStats, index: number): number {
  const perUnit = PLANETS[index]!.ores.reduce((sum, o) => sum + o.share * sellPrice(state, stats, o.id), 0);
  return throughput(state, stats, index) * perUnit;
}

export function fleetIncome(state: GameState, stats: FleetStats): number {
  return state.run.planets.reduce((sum, p, i) => sum + (p.owned ? planetIncome(state, stats, i) : 0), 0);
}

// ── Costs ──────────────────────────────────────────────────────────────────

/** Fleet discount (fittings, Matrix, Drift): negative makes things dearer. */
function discountMult(stats: FleetStats): number {
  return 1 - Math.min(DISCOUNT_CAP, stats.discount);
}

export function unlockCost(state: GameState, stats: FleetStats, index: number): number {
  return PLANETS[index]!.unlockCost * discountMult(stats) * (1 - roomCut(state, 'unlockCost'));
}

/** IPM: unlock ÷ 20 for the first upgrade, ×1.3 per level after, for all three stats. */
function upgradeBase(state: GameState, stats: FleetStats, index: number): number {
  return PLANETS[index]!.unlockCost * UPGRADE_PRICE_FRACTION * discountMult(stats) * (1 - roomCut(state, 'upgradeCost'));
}

/** Cost of `count` levels starting from `level`. The stat does not change the price, as in IPM. */
export function bulkUpgradeCost(state: GameState, stats: FleetStats, index: number, _stat: PlanetStat, level: number, count: number): number {
  const g = UPGRADE_PRICE_GROWTH;
  return upgradeBase(state, stats, index) * Math.pow(g, level - 1) * (Math.pow(g, count) - 1) / (g - 1);
}

/** Most levels affordable with `gc`, capped at `cap`. */
export function affordableLevels(state: GameState, stats: FleetStats, index: number, _stat: PlanetStat, level: number, gc: number, cap = 1000): number {
  const g = UPGRADE_PRICE_GROWTH;
  const first = upgradeBase(state, stats, index) * Math.pow(g, level - 1);
  if (gc < first) return 0;
  const n = Math.floor(Math.log(gc * (g - 1) / first + 1) / Math.log(g));
  return Math.max(0, Math.min(cap, n));
}

// ── Prices and workshops ───────────────────────────────────────────────────

/** Base GC per unit with Drift and the Sales room, before market and sell bonuses. */
export function unitValue(state: GameState, id: string): number {
  const def = resource(id);
  const drift = driftDef(state.run.drift);
  let value = def.value;
  if (def.kind === 'ore') value *= drift?.oreValue?.[id] ?? 1;
  if (def.kind === 'item') value *= drift?.itemValue ?? 1;
  if (def.kind !== 'ore') value *= roomMult(state, 'refinedValue');
  return value;
}

/** Bar and item value bonuses (projects, fittings, Matrix) are fleet stats, applied at sale. */
function refinedBonus(stats: FleetStats, id: string): number {
  const kind = resource(id).kind;
  return kind === 'alloy' ? m(stats.alloyValue) : kind === 'item' ? m(stats.itemValue) : 1;
}

/** What one unit sells for right now. */
export function sellPrice(state: GameState, stats: FleetStats, id: string): number {
  return unitValue(state, id) * refinedBonus(stats, id) * m(stats.sell) * shipMult(state, 'sell') * marketMult(state, id);
}

export function smeltSpeed(state: GameState, stats: FleetStats): number {
  return m(stats.smelt) * m(stats.haste) * roomMult(state, 'smelt') * shipMult(state, 'smelt');
}

export function craftSpeed(state: GameState, stats: FleetStats): number {
  return m(stats.craft) * m(stats.haste) * roomMult(state, 'craft') * shipMult(state, 'craft');
}

/** Expected bonus units per completed unit. */
export function smeltYield(stats: FleetStats): number {
  return Math.max(0, Math.min(YIELD_CAP, stats.smeltYield));
}

export function craftYield(stats: FleetStats): number {
  return Math.max(0, Math.min(YIELD_CAP, stats.craftYield));
}
